import { z, EnumLike } from 'zod'
import {
  formatDateTimeForApi,
  formatDateForApi,
  fromDataTimeStringApi,
  fromDateStringApi,
} from '~/shared/lib/date'
import { buildI18nMessage } from '~/shared/lib/i18nMessageHelper'
import { sanitizePhoneNumber, sanitizeStringNumber } from '~/shared/lib/utils'
import { getPhoneLengthByCountry } from '../config/countryConfig'

export const dateSchema = z
  .preprocess(
    (arg) => {
      if (typeof arg == 'string') {
        return fromDateStringApi(arg)
      }
      return arg
    },
    z.date({
      invalid_type_error: buildI18nMessage('validate.required field'),
    }),
  )
  .transform(formatDateForApi)

export const currentDateTimeSchema = z
  .preprocess(
    (arg, ctx) => {
      if (!arg) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: buildI18nMessage('validate.required field'),
        })
      }
      if (typeof arg === 'string') {
        return fromDataTimeStringApi(arg)
      }
      return arg
    },
    z.date({
      invalid_type_error: buildI18nMessage('validate.required field'),
    }),
  )
  .transform(formatDateTimeForApi)

export const dateOptionalSchema = z
  .preprocess(
    (arg) => {
      if (typeof arg == 'string') {
        return fromDateStringApi(arg)
      }
      return arg
    },
    z.date({
      invalid_type_error: buildI18nMessage('validate.required field'),
    }),
  )
  .transform(formatDateForApi)
  .or(z.string().max(0))

export const phoneScheme = z
  .string()
  .superRefine((value, ctx) => {
    const sanitized = sanitizePhoneNumber(value)

    if (!sanitized.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: buildI18nMessage('validate.required field'),
      })
    }

    if (sanitized.length > 0 && sanitized.length < getPhoneLengthByCountry()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: buildI18nMessage('validate.invalid phone number'),
      })
    }
  })
  .transform(sanitizePhoneNumber)

export const priceRequiredScheme = z
  .string()
  .or(z.number())
  .superRefine((value, ctx) => {
    const sanitized = sanitizeStringNumber(value)
    if (!sanitized) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: buildI18nMessage('validate.required field'),
      })
    }
    if (sanitized > 99999999) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: buildI18nMessage('validate.max cost exceeded'),
      })
    }
  })
  .transform(sanitizeStringNumber)

export const priceRequiredWithZeroScheme = z
  .string()
  .or(z.number())
  .superRefine((value, ctx) => {
    const sanitized = sanitizeStringNumber(value)
    if (!sanitized && sanitized !== 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: buildI18nMessage('validate.required field'),
      })
    }
    if (sanitized > 99999999) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: buildI18nMessage('validate.max cost exceeded'),
      })
    }
  })
  .transform(sanitizeStringNumber)

export const optionSchema = z.object({
  id: z.string(),
  label: z.string(),
})
export const uuidOptionSchema = z
  .union([z.string().uuid(), optionSchema])
  .transform((arg) => {
    if (typeof arg === 'string') {
      return arg
    }
    return arg.id
  })
export const uuidOptionsSchema = z.array(uuidOptionSchema)

export const enumOptionSchema = <T extends EnumLike>(values: T) =>
  z
    .union([
      z.nativeEnum(values),
      z.object({
        id: z.nativeEnum(values),
        label: z.string(),
      }),
    ])
    .transform((arg) => {
      if (typeof arg === 'object' && 'id' in arg) {
        return arg.id
      }
      return arg
    })

export const yearSchema = z
  .union([
    z
      .string()
      .min(1, buildI18nMessage('validate.required field'))
      .regex(/^\d{4}$/, buildI18nMessage('validate.invalid year format')),
    z.number(),
  ])
  .transform((arg) => {
    if (typeof arg === 'string') return parseInt(arg)
    return arg
  })

const yearsSeparatedByCommasRegex = /^(?:\d{4}(?:,\s*|$))+$/
export const yearsSeparatedByCommasSchema = () =>
  z
    .string()
    .regex(
      yearsSeparatedByCommasRegex,
      buildI18nMessage('validate.invalid years format'),
    )
    .transform((arg) => {
      return arg
        .split(',')
        .map((num) => Number(num.trim()))
        .filter(Number)
    })

export const numberRequiredScheme = z.coerce
  .number({
    invalid_type_error: buildI18nMessage('validate.required field'),
  })
  .min(0.01, buildI18nMessage('validate.required field'))

export const stringRequiredScheme = z
  .string()
  .trim()
  .min(1, buildI18nMessage('validate.required field'))

export const stringRequiredCountCharacterScheme = z
  .string()
  .trim()
  .min(1, buildI18nMessage('validate.required field'))

export const okpoRequiredSchema = z
  .string()
  .trim()
  .regex(/^\d{8}$|^\d{10}$|^\d{0}$/, buildI18nMessage('validate.okpo length'))

export const innRequiredSchema = z
  .string()
  .trim()
  .regex(/^\d{10}$|^\d{12}$/, buildI18nMessage('validate.inn length'))

export const kppRequiredSchema = z
  .string()
  .trim()
  .regex(/^\d{0}$|^\d{9}$/, buildI18nMessage('validate.kpp length'))

export const ogrnRequiredSchema = z
  .string()
  .trim()
  .regex(/^\d{13}$|^\d{15}$/, buildI18nMessage('validate.ogrn length'))

export const innCiRequiredSchema = z
  .string()
  .trim()
  .regex(/^\d{7}[A-Za-z]$/, buildI18nMessage('validate.inn ci length'))

export const ogrnCiRequiredSchema = z
  .string()
  .trim()
  .min(1, buildI18nMessage('validate.required field'))
  .max(32, buildI18nMessage('validate.ogrn ci length'))

export const bankRsRequiredSchema = z
  .string()
  .trim()
  .refine(
    (value) => /^\d{20}$/.test(value),
    buildI18nMessage('validate.field length 20'),
  )

export const bankBikRequiredSchema = z
  .string()
  .trim()
  .refine(
    (value) => /^\d{9}$/.test(value),
    buildI18nMessage('validate.bik length'),
  )

function validateSnils(snils: string) {
  // www.kholenkov.ru/data-validation/snils/#:~:text=%D0%9E%D0%BF%D0%B8%D1%81%D0%B0%D0%BD%D0%B8%D0%B5,%2D%D1%8F%20%D1%86%D0%B8%D1%84%D1%80%D1%8B%20%E2%80%94%20%D0%BA%D0%BE%D0%BD%D1%82%D1%80%D0%BE%D0%BB%D1%8C%D0%BD%D0%BE%D0%B5%20%D1%87%D0%B8%D1%81%D0%BB%D0%BE

  if (!snils.length) {
    return buildI18nMessage('validate.snils required')
  } else if (snils.length !== 11) {
    return buildI18nMessage('validate.snils digits only')
  } else {
    let sum = 0
    for (let i = 0; i < 9; i++) {
      sum += Number(snils[i]) * (9 - i)
    }
    let checkDigit = 0
    if (sum < 100) {
      checkDigit = sum
    } else if (sum > 101) {
      checkDigit = Number(sum % 101)
      if (checkDigit === 100) {
        checkDigit = 0
      }
    }
    if (checkDigit !== Number(snils.slice(-2))) {
      return buildI18nMessage('validate.snils checksum error')
    }
  }
}

export const snilsScheme = z
  .string()
  .superRefine((value, ctx) => {
    const sanitized = value.replace(/-/g, '')

    const errorMessage = validateSnils(sanitized)
    if (errorMessage) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: errorMessage,
      })
    }
  })
  .transform((value) => value.replace(/-/g, ''))

function validateOgrnip(ogrnip: string) {
  if (!ogrnip?.length) {
    return false
  } else if (ogrnip.length !== 15) {
    return buildI18nMessage('validate.ogrnip digits only')
  } else {
    const n15 = parseInt(
      (parseInt(ogrnip.slice(0, -1)) % 13).toString().slice(-1),
    )
    if (n15 !== parseInt(ogrnip[14])) {
      return buildI18nMessage('validate.ogrnip checksum error')
    }
  }
}

export const ogrnipScheme = z
  .string()
  .nullable()
  .optional()
  .or(z.literal(''))
  .superRefine((value, ctx) => {
    const sanitized = (value || '').replace(/-/g, '')

    const errorMessage = validateOgrnip(sanitized)
    if (errorMessage) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: errorMessage,
      })
    }
  })
  .transform((value) => (value || '').replace(/-/g, ''))

export const stsDocumentSeriesSchema = z
  .string()
  .trim()
  .refine(
    (value) => /^\d{4}$/.test(value),
    buildI18nMessage('validate.series length must be 4 digits only'),
  )

export const stsDocumentNumberSchema = z
  .string()
  .trim()
  .refine(
    (value) => /^\d{6}$/.test(value),
    buildI18nMessage('validate.number length must be 6 digits only'),
  )

export const ivoryStsDocumentNumberSchema = z
  .string()
  .trim()
  .min(1, buildI18nMessage('validate.required field'))

export const parkingCardNumberSchema = z
  .string()
  .trim()
  .refine(
    (value) => /^\d{10}$/.test(value),
    buildI18nMessage('validate.number length must be 10 digits only'),
  )
